import {
  Apps as DashboardIcon,
  AssignmentInd as ContactsIcon,
  Category as CategoryIcon,
  PermPhoneMsg as PermPhoneMsgIcon,
  Web as WebIcon,
} from "@mui/icons-material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useMatch } from "react-router-dom";
import { useUserData } from "../auth/useUserData.js";
import { useBubbleContext } from "../commons/hooks/useBubbleContext.js";
import { getUrlSearchParamsByParamsName } from "../commons/hooks/useStateWithUrlParams.js";
import { type IMainMenuItem } from "../commons/MainMenu.js";
import { BuildingIcon } from "../features/buildings/BuildingIcon.js";
import { DocumentIcon } from "../features/documents/DocumentIcon.js";
import { OpportunityIcon } from "../features/opportunities/OpportunityIcon.js";
import { OrganisationAvatar } from "../features/organisations/OrganisationAvatar.js";
import { PlanningIcon } from "../features/planning/PlanningIcon.js";
import { ProjectIcon } from "../features/projects/ProjectIcon.js";
import { useProjectIsOpportunity } from "../features/projects/useProjectIsOpportunity.js";
import { TaskIcon } from "../features/tasks/TaskIcon.js";
import { TemplateIcon } from "../features/templates/TemplateIcon.js";
import { UserAvatar } from "../features/users/UserAvatar.js";

export type MainMenuItem =
  | { name: "dashboard" }
  | { name: "buildings" }
  | { name: "opportunity" }
  | { name: "project" }
  | { name: "project2" }
  | { name: "documents" }
  | { name: "planning" }
  | { name: "tasks" }
  | { name: "CRM" }
  | { name: "templates" }
  | { name: "products" }
  | { name: "callassistant" }
  | { name: "webshopcms" }
  | { name: "myprofile" }
  | { name: "myprofile-user" }
  | { name: "myprofile-organisation" }
  | { name: "shop" }
  | { name: "agenda" }
  | { name: "meistersystemsadmin" }
  | { name: "organizations" }
  | { name: "login" }
  | { name: "signup" }
  | { name: "__otherprofile" };

type AppSidebarItem = MainMenuItem & Omit<IMainMenuItem, "name">;

export function useMainMenuItems() {
  const userData = useUserData();
  const viewer = userData.currentUser;
  const { bubble } = useBubbleContext();
  const { t } = useTranslate("SidebarItems");

  const projectMatch = useMatch(`/projects/:projectId/*`);
  const projectId = projectMatch?.params?.projectId;

  const { projectLoading, projectIsOpportunity } =
    useProjectIsOpportunity(projectId);

  const menuItems = React.useMemo(() => {
    if (!viewer) return { primaryItems: [], secondaryItems: [] };

    const items: (AppSidebarItem & { isHidden?: boolean })[] = [
      {
        name: "dashboard",
        label: t("Dashboard"),
        icon: <DashboardIcon />,
        path: "/dashboard",
      },
      {
        name: "buildings",
        label: t("Buildings"),
        icon: <BuildingIcon />,
        path: "/buildings",
        capability: "buildings",
      },
      {
        name: "callassistant",
        label: t("Call-Assistant"),
        icon: <PermPhoneMsgIcon />,
        path: "/callassistant",
        capability: "callAssistant",
      },
      {
        name: "opportunity",
        label: t("Opportunities"),
        icon: <OpportunityIcon />,
        path: "/opportunities",
        permission: "organisation_projects_create",
        bubbleAmount: 0, //bubble.opportunitiesTotal,
        isActive: match => {
          return Boolean(match) || (!projectLoading && projectIsOpportunity);
        },
        isHidden: !viewer.organisation.isCraftsmanOrganisation,
      },
      {
        name: "project",
        label: t("Projects"),
        icon: <ProjectIcon />,
        path: "/projects",
        bubbleAmount: bubble.projectsTotal,
        isActive: (match, location) =>
          Boolean(match) && !projectLoading && !projectIsOpportunity,
      },
      {
        name: "documents",
        label: t("Documents"),
        icon: <DocumentIcon />,
        path: "/documents",
      },
      {
        name: "planning",
        label: t("Planning and Resources"),
        icon: <PlanningIcon />,
        path: "/planning",
        capability: "planning",
      },
      {
        name: "tasks",
        label: t("Tasks"),
        icon: <TaskIcon />,
        path: {
          pathname: "/tasks",
          search: getUrlSearchParamsByParamsName(
            { status: ["OPEN"] },
            "filters"
          ).toString(),
        },
      },
      {
        name: "CRM",
        label: t("CRM"),
        icon: <ContactsIcon />,
        path: "/crm",
        permission: "crm_record_read",
      },
      {
        name: "templates",
        label: t("Templates"),
        icon: <TemplateIcon />,
        path: "/templates",
        capability: "templating",
        permission: "template_document_read",
      },
      {
        name: "products",
        label: t("Product catalogues"),
        icon: <CategoryIcon />,
        path: "/products",
        capability: "pim",
      },
      {
        name: "webshopcms",
        label: t("Web Shop CMS"),
        icon: <WebIcon />,
        path: "/webshopcms",
        capability: "webshopcms",
      },
    ] as const;

    const secondaryItems: (AppSidebarItem & { isHidden?: boolean })[] = [
      {
        name: "myprofile-organisation",
        path: "/organisation",
        label: viewer.organisation.title,
        icon: (
          <OrganisationAvatar
            organisationAvatar={viewer.organisation}
            size="s"
            style={{ left: -4, pointerEvents: "none" }}
          />
        ),
      },
      {
        name: "myprofile-user",
        path: "/user",
        label: `${viewer.firstname} ${viewer.familyname}`,
        icon: (
          <UserAvatar
            userAvatar={viewer}
            size="s"
            style={{ left: -4, pointerEvents: "none" }}
          />
        ),
      },
    ];

    return {
      primaryItems: items.filter(item => item.isHidden !== true),
      secondaryItems,
    };
  }, [bubble.projectsTotal, t, viewer, projectLoading, projectIsOpportunity]);

  return menuItems;
}
